/** Tab / Shift+Tab nesting for bullet and numbered lists in the compose body. */

const MAX_LIST_DEPTH = 8;

function isList(node: Element | null): node is HTMLUListElement | HTMLOListElement {
  return !!node && (node.tagName === "UL" || node.tagName === "OL");
}

function listParent(li: HTMLLIElement): HTMLUListElement | HTMLOListElement | null {
  const parent = li.parentElement;
  return isList(parent) ? parent : null;
}

function listItemFromNode(node: Node | null, editor: HTMLElement): HTMLLIElement | null {
  let walk: Node | null = node;
  while (walk && walk !== editor) {
    if (walk instanceof HTMLLIElement) return walk;
    walk = walk.parentNode;
  }
  return null;
}

function listDepth(li: HTMLElement, editor: HTMLElement): number {
  let depth = 0;
  let node = li.parentElement;
  while (node && node !== editor) {
    if (isList(node)) depth += 1;
    node = node.parentElement;
  }
  return depth;
}

function selectedListItems(editor: HTMLElement): HTMLLIElement[] {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return [];
  const range = sel.getRangeAt(0);
  const anchor = range.startContainer;
  if (!editor.contains(anchor) && !editor.contains(range.commonAncestorContainer)) return [];
  if (range.collapsed) {
    const li = listItemFromNode(anchor, editor);
    return li ? [li] : [];
  }
  const items = Array.from(editor.querySelectorAll("li")).filter((li) => {
    try {
      return range.intersectsNode(li);
    } catch {
      return false;
    }
  });
  return items.filter((li) => !items.some((other) => other !== li && other.contains(li)));
}

function followingLis(li: HTMLElement): HTMLLIElement[] {
  const items: HTMLLIElement[] = [];
  let sib = li.nextElementSibling;
  while (sib) {
    if (sib instanceof HTMLLIElement) items.push(sib);
    sib = sib.nextElementSibling;
  }
  return items;
}

/** List that should receive an indented sibling: the previous item's sublist. */
function listForIndent(prev: HTMLLIElement, tagName: string): HTMLElement {
  const last = prev.lastElementChild;
  if (
    last &&
    last.tagName === tagName &&
    (last instanceof HTMLUListElement || last instanceof HTMLOListElement)
  ) {
    return last;
  }
  const nest = document.createElement(tagName.toLowerCase());
  prev.appendChild(nest);
  return nest;
}

function indentListItem(li: HTMLLIElement, editor: HTMLElement): boolean {
  if (listDepth(li, editor) >= MAX_LIST_DEPTH) return false;
  const parent = listParent(li);
  if (!parent) return false;
  const prev = li.previousElementSibling;
  if (prev instanceof HTMLLIElement) {
    listForIndent(prev, parent.tagName).appendChild(li);
    return true;
  }
  const wrapper = document.createElement("li");
  wrapper.style.listStyle = "none";
  const nest = document.createElement(parent.tagName.toLowerCase());
  parent.insertBefore(wrapper, li);
  wrapper.appendChild(nest);
  nest.appendChild(li);
  return true;
}

function removeListIfEmpty(list: HTMLElement | null) {
  if (!isList(list)) return;
  if (list.querySelector("li")) return;
  const parent = list.parentElement;
  list.remove();
  if (parent instanceof HTMLLIElement) removeLiIfEmpty(parent);
}

function removeLiIfEmpty(li: HTMLLIElement) {
  if (li.querySelector("li")) return;
  if ((li.textContent || "").replace(/\u00a0/g, "").trim()) return;
  const parent = li.parentElement;
  li.remove();
  if (parent) removeListIfEmpty(parent);
}

function outdentListItem(li: HTMLLIElement): boolean {
  const parent = listParent(li);
  if (!parent || !parent.parentNode) return false;
  const grand = parent.parentElement;
  const following = followingLis(li);
  const grandList = grand?.parentElement ?? null;

  if (grand instanceof HTMLLIElement && isList(grandList)) {
    grandList.insertBefore(li, grand.nextSibling);
    if (following.length) {
      const nest = listForIndent(li, parent.tagName);
      for (const item of following) nest.appendChild(item);
    }
    removeListIfEmpty(parent);
    return true;
  }

  return liftOutOfList(li, parent, following);
}

function snapshotCaret(): { node: Node; offset: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  return { node: range.startContainer, offset: range.startOffset };
}

function restoreCaret(saved: { node: Node; offset: number } | null) {
  if (!saved?.node.isConnected) return;
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  try {
    const max =
      saved.node.nodeType === Node.TEXT_NODE
        ? (saved.node.textContent?.length ?? 0)
        : saved.node.childNodes.length;
    range.setStart(saved.node, Math.min(saved.offset, max));
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch {
    /* The browser dropped the node under the caret. */
  }
}

function liftOutOfList(
  li: HTMLLIElement,
  parent: HTMLUListElement | HTMLOListElement,
  following: HTMLLIElement[],
): boolean {
  const originalStart = parent.tagName === "OL" ? Number(parent.getAttribute("start")) || 1 : 1;
  const listType = parent.getAttribute("type");
  const block = document.createElement("div");
  while (li.firstChild) block.appendChild(li.firstChild);
  if (!block.childNodes.length) block.appendChild(document.createElement("br"));

  let tail: HTMLUListElement | HTMLOListElement | null = null;
  if (following.length) {
    tail = document.createElement(parent.tagName.toLowerCase()) as HTMLUListElement | HTMLOListElement;
    if (listType) tail.setAttribute("type", listType);
    for (const item of following) tail.appendChild(item);
  }

  li.remove();
  const headCount = Array.from(parent.children).filter((el) => el.tagName === "LI").length;
  if (!headCount) parent.replaceWith(block);
  else parent.after(block);
  if (tail) {
    if (tail instanceof HTMLOListElement) {
      const start = originalStart + headCount;
      if (start > 1) tail.start = start;
    }
    block.after(tail);
  }
  const sel = window.getSelection();
  if (!sel?.anchorNode?.isConnected) {
    const range = document.createRange();
    range.selectNodeContents(block);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
  return true;
}

/**
 * Indent or outdent the list item under the caret.
 * Returns false when the caret is not in a list, so Tab can move focus.
 */
export function handleListTab(editor: HTMLElement, shiftKey: boolean): boolean {
  const items = selectedListItems(editor);
  if (!items.length) return false;
  const caret = snapshotCaret();
  if (shiftKey) {
    for (const li of items) outdentListItem(li);
  } else {
    for (const li of items) indentListItem(li, editor);
  }
  restoreCaret(caret);
  return true;
}
